import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './TrustScore.css';

function TrustScore() {
  const [trustData, setTrustData] = useState(null);
  const [breakdown, setBreakdown] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchTrustScore();
  }, []);

  const fetchTrustScore = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get('/api/trust-score/me', { headers });
      setTrustData(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching trust score:', err);
      setError('Failed to load trust score');
      setLoading(false);
    }
  };

  const fetchBreakdown = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };
      
      const response = await axios.get('/api/trust-score/breakdown', { headers });
      setBreakdown(response.data);
      setShowBreakdown(true);
    } catch (err) {
      console.error('Error fetching breakdown:', err);
      setError('Failed to load breakdown');
    }
  };

  const getTrustLevelColor = (level) => {
    switch (level) {
      case 'Gold': return '#FFD700';
      case 'Silver': return '#C0C0C0';
      case 'Bronze': return '#CD7F32';
      default: return '#9E9E9E';
    }
  };

  const getTrustLevelIcon = (level) => {
    switch (level) {
      case 'Gold': return '🏆';
      case 'Silver': return '🥈';
      case 'Bronze': return '🥉';
      default: return '🌱';
    }
  };

  if (loading) {
    return (
      <div className="trust-score-container">
        <p>Loading trust score...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="trust-score-container">
        <p className="error">{error}</p>
      </div>
    );
  }

  if (!trustData) {
    return null;
  }

  const progressPercentage = (trustData.trustScore / trustData.nextLevelScore) * 100;

  return (
    <div className="trust-score-container">
      <h3>💜 Your Trust Score</h3>
      
      <div className="trust-score-card">
        <div className="trust-level-badge" style={{ backgroundColor: getTrustLevelColor(trustData.trustLevel) }}>
          <span className="trust-level-icon">{getTrustLevelIcon(trustData.trustLevel)}</span>
          <span className="trust-level-name">{trustData.trustLevel}</span>
        </div>
        
        <div className="trust-score-value">
          <span className="score-number">{trustData.trustScore}</span>
          <span className="score-max">/200</span>
        </div>
        
        <div className="trust-progress">
          <div className="progress-bar">
            <div 
              className="progress-fill" 
              style={{ 
                width: `${Math.min(progressPercentage, 100)}%`,
                backgroundColor: getTrustLevelColor(trustData.nextLevel)
              }}
            />
          </div>
          <p className="progress-text">
            {trustData.trustLevel !== 'Gold' && (
              <>
                {trustData.nextLevelScore - trustData.trustScore} points to {trustData.nextLevel}
              </>
            )}
            {trustData.trustLevel === 'Gold' && (
              <>You've reached the highest level! 🎉</>
            )}
          </p>
        </div>
        
        <div className="account-age">
          <span>📅 Account age: {trustData.accountAgeDays} days</span>
        </div>
      </div>

      <div className="discovery-limits-card">
        <h4>🔍 Today's Discovery Limits</h4>
        
        <div className="limit-item">
          <div className="limit-label">
            <span>Profile Views</span>
            <span className="limit-count">
              {trustData.limits.profilesViewedToday} / {trustData.limits.maxProfilesPerDay}
            </span>
          </div>
          <div className="limit-bar">
            <div 
              className="limit-fill"
              style={{ 
                width: `${(trustData.limits.profilesViewedToday / trustData.limits.maxProfilesPerDay) * 100}%`,
                backgroundColor: trustData.limits.profilesRemaining > 0 ? '#4CAF50' : '#f44336'
              }}
            />
          </div>
          <p className="limit-remaining">
            {trustData.limits.profilesRemaining} remaining today
          </p>
        </div>
        
        <div className="limit-item">
          <div className="limit-label">
            <span>Connection Requests</span>
            <span className="limit-count">
              {trustData.limits.requestsSentToday} / {trustData.limits.maxRequestsPerDay}
            </span>
          </div>
          <div className="limit-bar">
            <div 
              className="limit-fill"
              style={{ 
                width: `${(trustData.limits.requestsSentToday / trustData.limits.maxRequestsPerDay) * 100}%`,
                backgroundColor: trustData.limits.requestsRemaining > 0 ? '#4CAF50' : '#f44336'
              }}
            />
          </div>
          <p className="limit-remaining">
            {trustData.limits.requestsRemaining} remaining today
          </p>
        </div>
        
        <p className="limits-reset-info">
          ⏰ Limits reset daily at midnight
        </p>
      </div>

      <div className="trust-actions">
        <button 
          className="btn-secondary"
          onClick={() => showBreakdown ? setShowBreakdown(false) : fetchBreakdown()}
        >
          {showBreakdown ? 'Hide Details' : 'View Breakdown'}
        </button>
      </div>

      {showBreakdown && breakdown && (
        <div className="trust-breakdown">
          <h4>📊 Score Breakdown</h4>
          
          <div className="breakdown-section">
            <h5>✅ Verification ({breakdown.verification.emailVerified.points} points)</h5>
            <div className="breakdown-item">
              <span>Email Verified</span>
              <span className={breakdown.verification.emailVerified.value ? 'positive' : 'neutral'}>
                {breakdown.verification.emailVerified.value ? '+20' : '0'} points
              </span>
            </div>
          </div>
          
          <div className="breakdown-section">
            <h5>👤 Profile ({breakdown.profile.hasGender.points} points)</h5>
            <div className="breakdown-item">
              <span>Gender Specified</span>
              <span className={breakdown.profile.hasGender.value ? 'positive' : 'neutral'}>
                {breakdown.profile.hasGender.value ? '+5' : '0'} points
              </span>
            </div>
          </div>
          
          <div className="breakdown-section">
            <h5>📈 Activity ({breakdown.activity.accountAgeDays.points + breakdown.activity.connectionsCount.points} points)</h5>
            <div className="breakdown-item">
              <span>Account Age ({breakdown.activity.accountAgeDays.value} days)</span>
              <span className="positive">+{breakdown.activity.accountAgeDays.points} points</span>
            </div>
            <div className="breakdown-item">
              <span>Connections ({breakdown.activity.connectionsCount.value})</span>
              <span className="positive">+{breakdown.activity.connectionsCount.points} points</span>
            </div>
          </div>
          
          {(breakdown.negative.reportsReceived.value > 0 || breakdown.negative.blocksReceived.value > 0) && (
            <div className="breakdown-section negative-section">
              <h5>⚠️ Negative Factors ({breakdown.negative.reportsReceived.points + breakdown.negative.blocksReceived.points} points)</h5>
              {breakdown.negative.reportsReceived.value > 0 && (
                <div className="breakdown-item">
                  <span>Reports Received ({breakdown.negative.reportsReceived.value})</span>
                  <span className="negative">{breakdown.negative.reportsReceived.points} points</span>
                </div>
              )}
              {breakdown.negative.blocksReceived.value > 0 && (
                <div className="breakdown-item">
                  <span>Blocks Received ({breakdown.negative.blocksReceived.value})</span>
                  <span className="negative">{breakdown.negative.blocksReceived.points} points</span>
                </div>
              )}
            </div>
          )}
          
          <div className="breakdown-total">
            <strong>Total Score:</strong>
            <strong>{breakdown.total} points</strong>
          </div>
        </div>
      )}

      <div className="trust-info">
        <h4>💡 How to Increase Your Trust Score</h4>
        <ul>
          <li>✅ Email verified at registration (+20 points)</li>
          <li>👤 Specify your gender (+5 points)</li>
          <li>🤝 Make genuine connections (+2 points each, max 20)</li>
          <li>⏳ Stay active over time (+1 point per day, max 30)</li>
          <li>🛡️ Avoid reports and blocks (maintain good behavior)</li>
        </ul>
        
        <p className="trust-note">
          <strong>Note:</strong> Higher trust scores unlock more discovery matches and connection requests per day. 
          This helps keep the community safe while rewarding genuine members.
        </p>
      </div>
    </div>
  );
}

export default TrustScore;
